import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { executeAdHocQuery } from '../api/client';
import { formatNumber } from '../lib/format';

interface QueryResult {
  columns: string[];
  rows: Record<string, any>[];
  row_count: number;
  execution_time_ms: number;
}

export const QueryBuilder: React.FC = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exampleQueries = [
    {
      label: t('queryBuilder.exampleDailyGgr'),
      query: JSON.stringify({
        table: 'agg_casino_daily',
        select: ['event_date', 'sumMerge(bet_amount_real) as turnover', 'sumMerge(ggr) as ggr'],
        where: { event_date: { gte: 'today() - 7' } },
        group_by: ['event_date'],
        order_by: ['event_date'],
      }, null, 2),
    },
    {
      label: t('queryBuilder.exampleTopPlayers'),
      query: JSON.stringify({
        table: 'agg_finance_daily',
        select: ['player_id', 'sumMerge(deposit_amount) as total_deposits'],
        where: { event_date: { gte: 'today() - 30' } },
        group_by: ['player_id'],
        order_by: ['-total_deposits'],
        limit: 10,
      }, null, 2),
    },
    {
      label: t('queryBuilder.exampleActivePlayers'),
      query: JSON.stringify({
        table: 'agg_player_daily',
        select: ['site_id', 'uniqMerge(active_players) as active_players'],
        where: { event_date: { gte: 'today() - 1' } },
        group_by: ['site_id'],
        order_by: ['-active_players'],
      }, null, 2),
    },
  ];

  const executeQuery = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const parsedQuery = JSON.parse(query);
      const response = await executeAdHocQuery(parsedQuery);
      setResult(response.data);
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setError(t('queryBuilder.invalidJson'));
      } else {
        setError(err.response?.data?.error?.message || err.message || t('queryBuilder.queryExecutionFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('queryBuilder.title')}</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">{t('queryBuilder.subtitle')}</p>
      </div>

      {/* Example Queries */}
      <div className="flex flex-wrap gap-2">
        {exampleQueries.map((eq) => (
          <button key={eq.label} onClick={() => setQuery(eq.query)}
            className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {eq.label}
          </button>
        ))}
      </div>

      {/* Query Editor */}
      <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h2 className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('queryBuilder.queryJsonFormat')}</h2>
        </div>
        <textarea value={query} onChange={(e) => setQuery(e.target.value)}
          rows={12} placeholder='{\n  "table": "agg_casino_daily",\n  "select": ["event_date", "sumMerge(ggr) as ggr"],\n  "where": {"event_date": {"gte": "today() - 7"}},\n  "group_by": ["event_date"],\n  "order_by": ["event_date"]\n}'
          className="w-full bg-slate-50 p-4 font-mono text-sm text-slate-900 focus:outline-none dark:bg-slate-900 dark:text-white"
          spellCheck={false} />
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 dark:border-slate-700">
          <button onClick={executeQuery} disabled={loading || !query.trim()}
            className="rounded-lg bg-accent px-6 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-50">
            {loading ? t('queryBuilder.executing') : t('queryBuilder.runQuery')}
          </button>
          {result && (
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {result.row_count} {t('queryBuilder.rowsInMs')} {result.execution_time_ms}ms
            </span>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && result.rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                {result.columns.map((col) => (
                  <th key={col} className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {result.rows.map((row, i) => (
                <tr key={i} className="bg-white dark:bg-slate-900">
                  {result.columns.map((col) => (
                    <td key={col} className="px-4 py-3 text-slate-900 dark:text-white">
                      {typeof row[col] === 'number' ? formatNumber(row[col] as number, 2) : String(row[col] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
